import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from './auth-store';

/**
 * Requires an active session. Redirects to `/login` otherwise.
 */
export const authGuard: CanActivateFn = () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  return authStore.isAuthenticated() || router.createUrlTree(['/login']);
};

/**
 * Requires an active session with the given role. Redirects to `/login` when logged out, or to
 * the home page when logged in as the wrong role.
 */
export function roleGuard(role: 'admin' | 'debutant'): CanActivateFn {
  return () => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    if (!authStore.isAuthenticated()) {
      return router.createUrlTree(['/login']);
    }

    return authStore.role() === role || router.createUrlTree(['/']);
  };
}

/**
 * Redirects to `/change-password` when the current account must change its password before
 * doing anything else (e.g. right after an admin-triggered reset). Apply to every authenticated
 * route except the change-password route itself.
 */
export const passwordChangeGuard: CanActivateFn = () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  return !authStore.requiresPasswordChange() || router.createUrlTree(['/change-password']);
};
